import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type ChipTone = 'ok' | 'warn' | 'info' | 'brand' | 'mute';

export const SEVERITY_LABEL: Record<Severity, 'CRIT' | 'HIGH' | 'MED' | 'LOW' | 'INFO'> = {
  critical: 'CRIT',
  high: 'HIGH',
  medium: 'MED',
  low: 'LOW',
  info: 'INFO',
};

const SEVERITY_CLASS: Record<Severity, string> = {
  critical: 'text-brand-text border-brand/40',
  high: 'text-brand-200 border-brand-200/30',
  medium: 'text-warn border-warn/30',
  low: 'text-info border-info/30',
  info: 'text-mute border-line',
};

const TONE_CLASS: Record<ChipTone, string> = {
  ok: 'text-ok border-ok/30',
  warn: 'text-warn border-warn/30',
  info: 'text-info border-info/30',
  brand: 'text-brand-text border-brand/40',
  mute: 'text-mute border-line',
};

export type ChipProps = {
  className?: string;
  /** Optional title attribute carrying the long form (e.g. `Critical`). */
  title?: string;
  /**
   * Screen-reader text that stands in for a terse visible label (e.g. a framework name behind its
   * number). Rendered as visually hidden text, because `aria-label` is prohibited on this role-less
   * span (axe aria-prohibited-attr, the gap journey 15c has to exclude for the toast region).
   */
  srLabel?: string;
} & (
  | { severity: Severity; tone?: never; children?: never }
  | { tone: ChipTone; severity?: never; children: ReactNode }
  | { tone?: never; severity?: never; children: ReactNode }
);

/**
 * Bordered chip (DESIGN_SYSTEM section 8.3, the only chip style in this port).
 * inline-flex h-5 items-center rounded-sm border px-1.5 font-mono text-2xs uppercase.
 */
export function Chip(props: ChipProps) {
  const { className, title, srLabel } = props;
  let toneClass = TONE_CLASS.mute;
  let content: ReactNode;
  let longName = title;
  if (props.severity) {
    toneClass = SEVERITY_CLASS[props.severity];
    content = SEVERITY_LABEL[props.severity];
    longName = longName ?? props.severity;
  } else {
    toneClass = props.tone ? TONE_CLASS[props.tone] : TONE_CLASS.mute;
    content = props.children;
  }
  return (
    <span
      title={longName}
      className={cn(
        'inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-sm border px-1.5 font-mono text-2xs uppercase tracking-wider',
        toneClass,
        className,
      )}
    >
      {srLabel ? <span className="sr-only">{srLabel}</span> : null}
      {srLabel ? <span aria-hidden="true">{content}</span> : content}
    </span>
  );
}
